import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Dna, MapPin, CalendarDays } from "lucide-react";
import { Reveal } from "../motion";

function PanelHead({ title, to, link }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <h2 className="font-display text-2xl lg:text-3xl text-ink leading-tight">{title}</h2>
      <Link to={to} className="inline-flex items-center gap-2 text-xs font-semibold text-forest whitespace-nowrap">
        <span className="gxr-link">{link}</span> <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" />
      </Link>
    </div>
  );
}

const THUMBS = [
  "linear-gradient(135deg,#1F362B,#2E4A3B 60%,#557A66)",
  "linear-gradient(135deg,#2E4A3B,#1F362B 60%,#B8944F)",
  "linear-gradient(135deg,#557A66,#2E4A3B 60%,#1F362B)",
];

export default function PubsEvents() {
  const { t } = useTranslation();
  const pubs = t("home.pubs.items", { returnObjects: true });
  const events = t("home.events.items", { returnObjects: true });

  return (
    <section className="surface-1 py-20 lg:py-24" data-testid="home-pubs-events">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Reveal>
          <div className="paper p-7 lg:p-9 h-full" data-testid="home-publications">
            <PanelHead title={t("home.pubs.title")} to="/house-of-wisdom" link={t("home.pubs.link")} />
            <ul className="mt-6 divide-y divide-line">
              {Array.isArray(pubs) && pubs.map((p, i) => (
                <li key={i} className="group flex items-center gap-4 py-4" data-testid={`home-pub-${i + 1}`}>
                  <span className="h-14 w-20 rounded-lg flex-shrink-0 flex items-center justify-center text-gold-light shadow-inner" style={{ background: THUMBS[i % 3] }}>
                    <Dna className="h-6 w-6 opacity-80 transition-transform duration-500 group-hover:rotate-12" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-[15px] lg:text-base text-ink leading-snug group-hover:text-forest transition-colors" dir="ltr">{p.title}</div>
                    <div className="mt-1 text-xs text-slate">{p.org}</div>
                  </div>
                  <span className="text-xs font-bold text-slate flex-shrink-0">{p.year}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="paper p-7 lg:p-9 h-full flex flex-col" data-testid="home-events">
            <PanelHead title={t("home.events.title")} to="/events" link={t("home.events.link")} />
            <ul className="mt-6 divide-y divide-line">
              {Array.isArray(events) && events.slice(0, 3).map((e, i) => (
                <li key={i} className="group flex items-center gap-4 py-4" data-testid={`home-event-${i + 1}`}>
                  <span className="h-14 w-14 rounded-lg flex-shrink-0 flex flex-col items-center justify-center bg-ivory border border-line text-forest transition-colors group-hover:bg-forest group-hover:text-white group-hover:border-forest">
                    <span className="font-display text-xl leading-none">{e.day}</span>
                    <span className="text-[10px] uppercase tracking-wide mt-0.5 opacity-80">{e.month}</span>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-[15px] lg:text-base text-ink leading-snug group-hover:text-forest transition-colors">{e.title}</div>
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-slate"><MapPin className="h-3 w-3 text-gold" />{e.place}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-auto pt-6">
              <Link to="/events" data-testid="home-events-cta" className="btn-gold-outline !py-2.5 !px-5 !text-[11px]">
                <CalendarDays className="h-3.5 w-3.5" /> {t("home.events.cta")}
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
